import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { AssigneeConfirmationDialog } from '@/components/AssigneeConfirmationDialog';
import { DownloadDropdown } from '@/components/DownloadDropdown';
import { ErrorContainer } from '@/components/ErrorContainer';
import { FormattedDateRelative } from '@/components/FormattedDateRelative';
import { GenericTable } from '@/components/GenericTable';
import { Breadcrumb, BreadcrumbPortal, HeaderActionsPortal } from '@/components/Header';
import { QuickFilters } from '@/components/QuickFilters';
import { StatusConfirmationDialog } from '@/components/StatusConfirmationDialog';
import { createCasesTableConfig } from '@/components/TableBuilder';
import { Skeleton } from '@/components/ui/skeleton';
import { StaticMultiSelect } from '@/components/ui/static-trigger-multi-select';
import { UserCell } from '@/components/UserCell';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import { useActiveFiltersConfig } from '@/hooks/useActiveFiltersConfig';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { useCampaign } from '@/hooks/useCampaigns';
import { useCasesByWorkflow } from '@/hooks/useCases';
import { useColumnCustomization } from '@/hooks/useColumnCustomization';
import { useCursorPagination } from '@/hooks/useCursorPagination';
import { useDownloadCaseRecords } from '@/hooks/useDownloadCaseRecords';
import { useUsersByIds } from '@/hooks/useUsers';
import { useWorkflowsByProgram } from '@/hooks/useWorkflows';
import { useStore } from '@/store';
import { getErrorButtonConfig } from '@/utils/errorButtonConfig';

export const CampaignDetails = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const {
    data: campaign,
    isLoading: campaignLoading,
    error: campaignError,
  } = useCampaign(campaignId || '');

  const [activeQuickFilter, setActiveQuickFilter] = useState('all');
  const navigate = useNavigate();
  const {
    selectedProgramId,
    testModeEnabled,
    campaignDetailsPageSize,
    setCampaignDetailsPageSize,
  } = useStore();

  // Use cursor pagination hook with reset dependencies
  const { currentPage, handlePageChange, getCurrentCursor } = useCursorPagination({
    pageType: 'campaignDetails',
    resetDependencies: [activeQuickFilter, campaignId, campaignDetailsPageSize],
  });

  // Get workflow loading state to prevent flash of empty state
  // Note: ProgramLayout also loads workflows, React Query caches the result
  const { isLoading: workflowsLoading } = useWorkflowsByProgram(selectedProgramId);

  const { getSelectedWorkflow, setSelectedWorkflowId } = useStore();
  const selectedWorkflow = getSelectedWorkflow();

  // Sync campaign's workflow to the store so the breadcrumb shows it
  useEffect(() => {
    if (campaign?.workflow_id) {
      setSelectedWorkflowId(campaign.workflow_id);
    }
  }, [campaign?.workflow_id, setSelectedWorkflowId]);

  // Get the appropriate filter config based on workflow type
  const activeFiltersConfig = useActiveFiltersConfig();

  // Use the custom hook for column configuration
  const { columnConfig, selectedFields, handleFieldSelectionChange } = useColumnCustomization();

  // Download hook - reuse existing case download hook
  const { downloadCaseRecords } = useDownloadCaseRecords();

  // Get the active filter configuration
  const activeFilterConfig = activeFiltersConfig.filters.find(f => f.id === activeQuickFilter);

  const {
    data: casesData,
    isLoading: casesLoading,
    error: casesError,
  } = useCasesByWorkflow({
    workflow_id: campaign?.workflow_id || '',
    campaign_id: campaignId,
    limit: campaignDetailsPageSize,
    start_after_id: getCurrentCursor(),
    states: activeFilterConfig?.filter.states,
  });

  const error = campaignError || casesError;

  // Use actual cases from API
  const cases = casesData?.case_records || [];
  const hasNextPage = casesData?.has_more || false;

  // Look up campaign creator details
  const createdByIds = useMemo(
    () => (campaign?.created_by ? [campaign.created_by] : []),
    [campaign?.created_by],
  );
  const { usersMap, isLoading: usersLoading } = useUsersByIds(createdByIds);
  const campaignCreator = campaign?.created_by ? usersMap?.get(campaign.created_by) : undefined;

  // Bulk selection hook for action handling
  const {
    handleIndividualCaseAction,
    handleBulkAssigneeSelect,
    statusConfirmationDialog,
    handleStatusConfirmationClose,
    handleStatusConfirmationConfirm,
    bulkAssigneeUpdateModal,
    handleConfirmBulkAssigneeUpdate,
    handleCancelBulkAssigneeUpdate,
  } = useBulkSelection({ cases });

  const tableConfig = useMemo(
    () =>
      createCasesTableConfig(
        selectedWorkflow,
        columnConfig,
        handleIndividualCaseAction,
        (assigneeUserId, caseId, teamId) =>
          handleBulkAssigneeSelect(assigneeUserId, new Set([caseId]), teamId),
        item => `../cases/${item.id}`, // Navigate to case details page
        () => ({ fromCampaign: campaignId }), // Pass campaign context for breadcrumb
        undefined, // usersMap
        undefined, // usersLoading
        ['created_by'], // Exclude — campaign creator is shown in the header card
      ),
    [
      selectedWorkflow,
      columnConfig,
      campaignId,
      handleIndividualCaseAction,
      handleBulkAssigneeSelect,
    ],
  );

  // Store the last case ID of current page for next page navigation
  const lastCaseId = cases.length > 0 ? cases[cases.length - 1]?.id : undefined;

  // Render breadcrumb early - before any error returns
  const breadcrumb = (
    <BreadcrumbPortal>
      <Breadcrumb>
        <Breadcrumb.Item to="../campaigns">Campaigns</Breadcrumb.Item>
        <Breadcrumb.Item>{campaignId}</Breadcrumb.Item>
      </Breadcrumb>
    </BreadcrumbPortal>
  );

  // Render header actions early (but disable during loading)
  const headerActions = (
    <HeaderActionsPortal>
      {!testModeEnabled && (
        <DownloadDropdown
          onDownload={async (format, callsOption, includeEligibilityChecks) => {
            if (!campaign) return;
            await downloadCaseRecords(
              selectedProgramId!,
              {
                campaign_id: campaignId,
                states: activeFilterConfig?.filter.states,
              },
              format,
              includeEligibilityChecks || false,
              campaign?.name,
              callsOption || 'none',
              selectedWorkflow?.workflow_type,
            );
          }}
          disabled={!selectedProgramId || !campaign || campaignLoading || cases.length === 0}
          workflowType={selectedWorkflow?.workflow_type}
          onlyShowIcon={true}
        />
      )}
    </HeaderActionsPortal>
  );

  // Show loading if campaign or workflows are still loading
  if (campaignLoading || workflowsLoading) {
    return (
      <>
        {breadcrumb}
        {headerActions}
        <div className="flex flex-col h-full">
          {/* Campaign Info Card Skeleton */}
          <div className="px-6 pt-3">
            <div className="bg-card rounded-lg shadow-sm border px-3 py-3.5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-3 w-32" />
                </div>
                <div>
                  <Skeleton className="h-4 w-20 mb-2" />
                  <Skeleton className="h-3 w-28" />
                </div>
              </div>
            </div>
            <div className="flex justify-between items-center mt-4">
              <QuickFilters
                activeFilter={activeQuickFilter}
                onFilterChange={filterId => {
                  setActiveQuickFilter(filterId);
                }}
                workflowId=""
                filtersConfig={activeFiltersConfig}
                baseFilters={{
                  campaign_id: campaignId || undefined,
                }}
              />
              <StaticMultiSelect
                options={columnConfig.map(col => ({
                  label: col.label,
                  value: col.id,
                  group: col.group,
                }))}
                selected={selectedFields}
                onChange={handleFieldSelectionChange}
                placeholder="Customize columns"
              />
            </div>
          </div>

          {/* Empty table area while campaign loads */}
          <div className="flex-1 px-6 overflow-hidden">
            <div className="flex flex-col h-full min-h-0">
              <GenericTable
                config={tableConfig}
                data={[]}
                loading={true}
                error={null}
                currentPage={1}
                hasNextPage={false}
                onPageChange={() => {}}
              />
            </div>
          </div>
        </div>
      </>
    );
  }
  if (error || !campaign) {
    const buttonConfig = getErrorButtonConfig(error?.APIHttpStatus, navigate);
    return (
      <>
        {breadcrumb}
        {headerActions}
        <ErrorContainer
          errorMessage={error?.userMessage}
          buttonText={buttonConfig?.text}
          buttonOnClick={buttonConfig?.onClick}
        />
      </>
    );
  }

  return (
    <>
      {breadcrumb}
      {headerActions}

      <div className="flex flex-col h-full">
        {/* Campaign Info Card */}

        {/* Cases Table - Full height */}
        <div className="flex-1 px-6 overflow-hidden">
          {/* Quick Filters and Column Customization */}
          <div className="flex flex-col h-full min-h-0">
            <div className="py-3">
              <div className="bg-card rounded-lg shadow-xs border px-3 py-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Campaign Name</p>
                    <p className="text-sm">{campaign.name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Created by</p>
                    <div className="mt-1">
                      <UserCell
                        user={campaignCreator}
                        fallbackId={campaign.created_by}
                        loading={!!campaign.created_by && usersLoading}
                      />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Created at</p>
                    <p className="text-sm">
                      {campaign.created_at ? (
                        <FormattedDateRelative value={campaign.created_at} />
                      ) : (
                        'N/A'
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-between items-center mt-1">
              <QuickFilters
                activeFilter={activeQuickFilter}
                onFilterChange={filterId => {
                  setActiveQuickFilter(filterId);
                }}
                workflowId={campaign?.workflow_id || ''}
                filtersConfig={activeFiltersConfig}
                baseFilters={{
                  campaign_id: campaignId || undefined,
                }}
              />
              <StaticMultiSelect
                options={columnConfig.map(col => ({
                  label: col.label,
                  value: col.id,
                  group: col.group,
                }))}
                disabled={!!error}
                selected={selectedFields}
                onChange={handleFieldSelectionChange}
                placeholder="Customize columns"
              />
            </div>
            <GenericTable
              config={tableConfig}
              data={cases}
              loading={casesLoading}
              error={casesError}
              currentPage={currentPage}
              hasNextPage={hasNextPage}
              onPageChange={newPage => handlePageChange(newPage, lastCaseId)}
              pageSize={campaignDetailsPageSize}
              pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
              onPageSizeChange={setCampaignDetailsPageSize}
            />
          </div>
        </div>

        {/* Status Confirmation Dialog */}
        <StatusConfirmationDialog
          isOpen={statusConfirmationDialog.isOpen}
          onClose={handleStatusConfirmationClose}
          onConfirm={handleStatusConfirmationConfirm}
          statusUpdatePlans={statusConfirmationDialog.statusUpdatePlans}
          workflow={selectedWorkflow}
          cases={cases}
          isIndividual={statusConfirmationDialog.isIndividual}
        />

        {/* Assignee Confirmation Dialog */}
        <AssigneeConfirmationDialog
          isOpen={bulkAssigneeUpdateModal.isOpen}
          onClose={handleCancelBulkAssigneeUpdate}
          onConfirm={handleConfirmBulkAssigneeUpdate}
          assigneeUpdatePlan={bulkAssigneeUpdateModal.assigneeUpdatePlan}
          cases={cases}
          isIndividual={bulkAssigneeUpdateModal.assigneeUpdatePlan?.count === 1}
        />
      </div>
    </>
  );
};
